/* Plan manager type-ahead for the "plan_manager_search" form field type.
 *
 * Searches the plan manager directory by name or email and, on selection, fills
 * the two sibling text fields named in data-target-name / data-target-email
 * (normally plan_manager_name / plan_manager_email). Those fields carry the
 * profile mappings, so the values flow through to the user record as usual.
 *
 * Deliberately vanilla rather than an Alpine component: public forms render on
 * the non-admin layout where the Alpine init path can stall (see the comments
 * around initFormOther in application.js).
 */

const DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 3; // mirrors the server floor, so short queries never hit the network

function escapeHtml(value) {
  const div = document.createElement('div');
  div.textContent = value == null ? '' : String(value);
  return div.innerHTML;
}

function setupWidget(widget) {
  const input = widget.querySelector('[data-pm-input]');
  const list = widget.querySelector('[data-pm-results]');
  const status = widget.querySelector('[data-pm-status]');
  const targetId = widget.dataset.targetId;
  const selectionRequired = widget.dataset.selectionRequired === 'true';
  if (!input || !list) return;

  const searchUrl = widget.dataset.searchUrl || '/plan-managers/search';
  let results = [];
  let activeIndex = -1;
  let debounceTimer = null;
  let inFlight = null;

  function showStatus(message) {
    if (!status) return;
    status.textContent = message;
    status.classList.toggle('hidden', !message);
  }

  function close() {
    list.classList.add('hidden');
    list.innerHTML = '';
    input.setAttribute('aria-expanded', 'false');
    results = [];
    activeIndex = -1;
  }

  function highlight() {
    Array.from(list.children).forEach((li, index) => {
      const active = index === activeIndex;
      li.classList.toggle('bg-blue-50', active);
      li.classList.toggle('dark:bg-gray-700', active);
      li.setAttribute('aria-selected', active ? 'true' : 'false');
    });
  }

  function render() {
    if (!results.length) {
      list.innerHTML =
        '<li class="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">' +
        (selectionRequired ? 'No matching plan manager found.' : 'No match — type the details in the fields below.') +
        '</li>';
      list.classList.remove('hidden');
      input.setAttribute('aria-expanded', 'true');
      return;
    }

    list.innerHTML = results
      .map((pm, index) => {
        const detail = [pm.organisation, pm.email].filter(Boolean).join(' · ');
        return (
          `<li role="option" aria-selected="false" data-index="${index}" ` +
          'class="px-3 py-2 cursor-pointer text-sm hover:bg-blue-50 dark:hover:bg-gray-700">' +
          `<span class="font-medium block">${escapeHtml(pm.name)}</span>` +
          (detail ? `<span class="text-gray-500 dark:text-gray-400">${escapeHtml(detail)}</span>` : '') +
          '</li>'
        );
      })
      .join('');

    list.classList.remove('hidden');
    input.setAttribute('aria-expanded', 'true');
    activeIndex = -1;
  }

  // Fill the sibling inputs. Look up by input name first (the authoritative
  // handle), falling back to the id the form partial always derives from the
  // field name. Never disable them — manual correction has to stay possible.
  function fillTargets(planManager) {
    const scope = widget.closest('form') || document;

    if (targetId) {
      const idInput = scope.querySelector(`#${targetId}`);
      if (idInput) idInput.value = planManager.id;
    }

    const set = (fieldName, value) => {
      if (!fieldName) return;
      const el =
        scope.querySelector(`[name="submission[${fieldName}]"]`) ||
        scope.querySelector(`#field_${fieldName}`);
      if (!el) return;

      el.value = value || '';
      // Let Alpine x-model, form_conditional.js and any dirty tracking see it.
      el.dispatchEvent(new Event('input', {bubbles: true}));
      el.dispatchEvent(new Event('change', {bubbles: true}));
    };

    set(widget.dataset.targetName, planManager.name);
    set(widget.dataset.targetEmail, planManager.email);
  }

  function select(index) {
    const planManager = results[index];
    if (!planManager) return;

    fillTargets(planManager);
    input.value = planManager.name;
    close();
    showStatus(
      `Selected: ${planManager.name}${planManager.email ? ` — ${planManager.email}` : ''}. ` +
        (selectionRequired ? '' : 'Edit the fields below if anything is wrong.')
    );
  }

  function search(query) {
    // Abort the previous request so a slow early response can't overwrite a
    // later one.
    if (inFlight) inFlight.abort();
    inFlight = new AbortController();

    fetch(`${searchUrl}?q=${encodeURIComponent(query)}`, {
      headers: {Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest'},
      signal: inFlight.signal
    })
      .then(response => {
        if (response.status === 429) {
          showStatus('Too many searches — please wait a moment.');
          close();
          return null;
        }
        if (!response.ok) return null;
        return response.json();
      })
      .then(data => {
        if (data === null) return;
        results = Array.isArray(data) ? data : [];
        render();
      })
      .catch(error => {
        // Aborts are expected; anything else just leaves manual entry working.
        if (error.name !== 'AbortError') close();
      });
  }

  input.addEventListener('input', () => {
    showStatus('');
    clearTimeout(debounceTimer);

    // Typing after a selection invalidates it. A valid directory id is only
    // restored by choosing one of the returned options.
    if (targetId) {
      const scope = widget.closest('form') || document;
      const idInput = scope.querySelector(`#${targetId}`);
      if (idInput) idInput.value = '';
    }

    const query = input.value.trim();
    if (query.length < MIN_QUERY_LENGTH) {
      if (inFlight) inFlight.abort();
      close();
      return;
    }

    debounceTimer = setTimeout(() => search(query), DEBOUNCE_MS);
  });

  input.addEventListener('keydown', event => {
    const open = !list.classList.contains('hidden');

    switch (event.key) {
      case 'ArrowDown':
        if (!open || !results.length) return;
        event.preventDefault();
        activeIndex = (activeIndex + 1) % results.length;
        highlight();
        break;
      case 'ArrowUp':
        if (!open || !results.length) return;
        event.preventDefault();
        activeIndex = activeIndex <= 0 ? results.length - 1 : activeIndex - 1;
        highlight();
        break;
      case 'Enter':
        if (!open || activeIndex < 0) return;
        event.preventDefault();
        select(activeIndex);
        break;
      case 'Escape':
      case 'Tab':
        close();
        break;
    }
  });

  list.addEventListener('mousedown', event => {
    const li = event.target.closest('li[data-index]');
    if (!li) return;
    event.preventDefault(); // keep focus so blur doesn't close before the click lands
    select(Number(li.dataset.index));
  });

  document.addEventListener('click', event => {
    if (!widget.contains(event.target)) close();
  });
}

export function initPlanManagerTypeahead() {
  document.querySelectorAll('[data-wps-plan-manager-search]').forEach(widget => {
    if (widget.dataset.pmBound === 'true') return;
    widget.dataset.pmBound = 'true';
    setupWidget(widget);
  });
}
